import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type ModalProps = {
  open: boolean
  onClose: () => void
  /** Accessible dialog title (rendered as the heading, wires `aria-labelledby`). */
  title: string
  /** Optional descriptive line under the title (wires `aria-describedby`). */
  description?: string
  /** Width preset; `lg` for the picker, `md` for forms/confirms. */
  size?: 'md' | 'lg'
  children: React.ReactNode
  /** Sticky footer (e.g. confirm/cancel actions); rendered below the body. */
  footer?: React.ReactNode
}

/**
 * App-styled modal built on `@radix-ui/react-dialog` so focus trapping,
 * `Esc`-to-close, `aria-modal`, `aria-labelledby` and focus restoration to
 * the trigger are handled correctly out of the box. The chrome mirrors the
 * existing `SessionExpiryModal` (`bg-black/60 backdrop-blur-sm`, rounded
 * card) and uses logical spacing so it mirrors cleanly under `dir="rtl"`.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
}: ModalProps) {
  const { t } = useTranslation('common')

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content
          dir="rtl"
          className={
            // Mobile: full-width bottom sheet. Desktop (sm+): centered card.
            // `left-1/2 -translate-x-1/2` is used (physical, not logical) so
            // the panel stays horizontally centered regardless of `dir`.
            'fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] flex-col rounded-t-3xl border border-brand-100 bg-white shadow-2xl focus:outline-none sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[88vh] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl ' +
            (size === 'lg'
              ? 'sm:w-[min(46rem,calc(100vw-2rem))]'
              : 'sm:w-[min(34rem,calc(100vw-2rem))]')
          }
          // Opt out of Radix's required-description warning when this modal
          // has no descriptive line; when it does, Radix wires it via Title.
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <header className="flex items-start justify-between gap-4 border-b border-brand-100 ps-6 pe-4 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-bold font-display text-brand-900 tracking-tight">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm text-brand-600 leading-relaxed">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              aria-label={t('cancel')}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 hover:text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto ps-6 pe-6 py-5">
            {children}
          </div>

          {footer ? (
            <footer className="border-t border-brand-100 ps-6 pe-6 py-4">
              {footer}
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
